import { useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import type { IncentivesResponse } from 'src/payments/hooks/use-incentives/use-incentives';

type IncentiveExposureParams = Pick<IncentivesResponse, 'isCashBack' | 'userType'> & {
  // Cash-back dollar amount or points-boost multiplier label (e.g. "2x"); stringified on emission.
  incentiveValue?: IncentivesResponse['incentiveValue'] | string;
  // Which surface the exposure fired on (e.g. checkout_payment_selection, order_confirmation,
  // product_description).
  source: string;
  // Distinguishes cash-back from points-boost exposure. Omitted for the legacy signup/discount
  // treatment, which is neither.
  rewardType?: 'cash_back' | 'points_boost';
};

// Emit "incentive exposure" once each time an incentive treatment becomes visible (Q5: do incentives drive
// enrollment / first payment), gated behind the rollout flag for a gradual rollout (serviceFeeShown precedent).
// Token-gated so the join key isn't empty before useDutchiePayAnalytics resolves; the ref resets when the
// incentive disappears so re-showing the treatment re-fires. Shared by every incentive banner/accordion surface.
export const useIncentiveExposure = ({
  incentiveValue,
  userType,
  isCashBack,
  source,
  rewardType,
}: IncentiveExposureParams): void => {
  const flags = useFlags();
  const { trackIncentiveExposure, hasSessionToken } = useDutchiePayAnalytics();
  const incentiveExposureTelemetryEnabled = flags['fintech.platform.incentive-exposure-telemetry.rollout'];
  const exposureTrackedRef = useRef(false);

  useEffect(() => {
    if (!incentiveValue) {
      exposureTrackedRef.current = false;
      return;
    }
    if (exposureTrackedRef.current || !hasSessionToken || !incentiveExposureTelemetryEnabled) {
      return;
    }
    exposureTrackedRef.current = true;
    trackIncentiveExposure({ userType, incentiveValue: String(incentiveValue), isCashBack, source, rewardType });
  }, [
    incentiveValue,
    userType,
    isCashBack,
    source,
    rewardType,
    hasSessionToken,
    incentiveExposureTelemetryEnabled,
    trackIncentiveExposure,
  ]);
};
